<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ChatwootButton from 'dashboard/components-next/button/Button.vue';
import ChatwootInput from 'dashboard/components-next/input/Input.vue';
import DropdownSelect from 'dashboard/components-next/dropdown-select/DropdownSelect.vue';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';

const BRAZIL_DIAL_CODE = '+55';
const normalizeClientStatus = status =>
  String(status).toLowerCase() === 'lead' ? 'cliente' : status;
const BRAZILIAN_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

export default {
  components: { ChatwootButton, ChatwootInput, DropdownSelect },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    // Aceleres -- container do painel de contato, o mesmo que a lista de
    // mídias usa. Sem ele cai no `body`, com o desenho antigo de gaveta.
    teleportTarget: {
      type: [Object, String],
      default: null,
    },
  },
  emits: ['cancel', 'delete', 'update:show'],
  data() {
    return {
      countries,
      brazilianStates: BRAZILIAN_STATES,
      name: '',
      nomeZoraxy: '',
      dialCode: BRAZIL_DIAL_CODE,
      basePhone: '',
      email: '',
      document: '',
      documentType: '',
      whatsapp: '',
      instagram: '',
      tiktok: '',
      facebook: '',
      telegram: '',
      twitter: '',
      linkedin: '',
      birthDate: '',
      birthDateDisplay: '',
      address: '',
      addressNumber: '',
      addressComplement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      notes: '',
      categoriaAbc: '',
      categoria: '',
      status: '',
      responsible: '',
      responsibleId: '',
      sellers: [],
      canChangeResponsible: false,
      categories: [],
      aceleresClient: null,
      aceleresClientRequest: 0,
      photoUrl: '',
      photoLoadFailed: false,
      zipCodeLookupSequence: 0,
      escapeKeyHandler: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    additionalCategories() {
      return this.categories.filter(
        category => !['Quente', 'Morno', 'Frio'].includes(category)
      );
    },
    documentTypeOptions() {
      return [
        { value: '', label: 'Selecione' },
        { value: 'cpf', label: 'CPF' },
        { value: 'cnpj', label: 'CNPJ' },
      ];
    },
    stateOptions() {
      const options = this.brazilianStates.map(uf => ({
        value: uf,
        label: uf,
      }));
      if (this.state && !this.brazilianStates.includes(this.state)) {
        options.unshift({ value: this.state, label: this.state });
      }
      return [{ value: '', label: 'Selecione...' }, ...options];
    },
    categoryOptions() {
      const categories = [
        'Quente',
        'Morno',
        'Frio',
        ...this.additionalCategories,
      ];
      if (this.categoria && !categories.includes(this.categoria)) {
        categories.push(this.categoria);
      }
      return [
        { value: '', label: 'Selecione' },
        ...categories.map(category => ({ value: category, label: category })),
      ];
    },
    abcOptions() {
      return [
        { value: '', label: 'Selecione' },
        { value: 'A', label: 'A - Alta prioridade' },
        { value: 'B', label: 'B - Média prioridade' },
        { value: 'C', label: 'C - Baixa prioridade' },
        { value: 'D', label: 'D - Inativo' },
      ];
    },
    statusOptions() {
      const options = [{ value: 'cliente', label: 'Cliente' }];
      if (this.status && !options.some(option => option.value === this.status)) {
        options.push({ value: this.status, label: this.status });
      }
      return options;
    },
    responsibleOptions() {
      if (!this.canChangeResponsible) {
        return [
          {
            value: '__current_responsible__',
            label: this.responsible || 'Sem responsável definido',
          },
        ];
      }

      const sellers = this.sellers.map(seller => ({
        value: String(seller.id),
        label: seller.name,
      }));
      if (
        this.responsibleId &&
        !sellers.some(option => option.value === this.responsibleId)
      ) {
        sellers.push({
          value: this.responsibleId,
          label: this.responsible || 'Responsável atual',
        });
      }

      return [
        { value: '', label: 'Selecione' },
        ...sellers,
      ];
    },
    responsibleDropdownValue() {
      return this.canChangeResponsible
        ? this.responsibleId
        : '__current_responsible__';
    },
    editContactTitle() {
      return this.$t('EDIT_CONTACT.TITLE');
    },
    backLabel() {
      return 'Voltar';
    },
    nameLabel() {
      return 'Nome';
    },
    clientPhotoSource() {
      return this.photoLoadFailed ? '' : this.photoUrl;
    },
    clientPhotoInitials() {
      const displayName = `${this.nomeZoraxy || this.name || ''}`.trim();
      if (!displayName) return '👤';
      return displayName
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase();
    },
    // JIDs de grupo não são telefones; preserve-os sem aplicar máscara local.
    isGroupContact() {
      return `${this.contact.identifier || ''}`.endsWith('@g.us');
    },
    documentError() {
      const digits = `${this.document || ''}`.replace(/\D/g, '');
      if (!digits) return '';

      if (this.documentType === 'cpf') {
        return this.isValidCpf(digits) ? '' : 'CPF inválido.';
      }
      if (this.documentType === 'cnpj') {
        return this.isValidCnpj(digits) ? '' : 'CNPJ inválido.';
      }
      if (this.isValidCpf(digits) || this.isValidCnpj(digits)) return '';
      return 'Documento inválido. Informe um CPF ou CNPJ válido.';
    },
    zipCodeError() {
      const digits = `${this.zipCode || ''}`.replace(/\D/g, '');
      if (!digits || digits.length === 8) return '';
      return 'Informe um CEP válido (ex.: 00000-000).';
    },
    birthDateError() {
      const digits = `${this.birthDateDisplay || ''}`.replace(/\D/g, '');
      if (!digits) return '';
      if (digits.length !== 8) return 'Informe uma data válida (DD/MM/AAAA).';

      const day = Number(digits.slice(0, 2));
      const month = Number(digits.slice(2, 4));
      const year = Number(digits.slice(4, 8));
      const date = new Date(year, month - 1, day);
      if (
        year < 1900 ||
        year > 2100 ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return 'Informe uma data válida (DD/MM/AAAA).';
      }
      return '';
    },
    canSubmit() {
      return (
        !!this.name.trim() &&
        !this.documentError &&
        !this.zipCodeError &&
        !this.birthDateError
      );
    },
  },
  watch: {
    // Observar o objeto inteiro (não só o id) reseta o formulário a cada
    // atualização do contato vinda da store (nova mensagem, leitura, etc.)
    // enquanto o drawer está aberto — apaga o que o usuário está digitando
    // e prende o Enviar (Nome volta vazio). Só o id importa aqui: é
    // a única mudança que exige recarregar os campos do zero.
    'contact.id': {
      handler() {
        this.setContactObject();
        this.loadAceleresClient();
      },
      immediate: true,
    },
    // A tecla so e ouvida enquanto o drawer esta aberto: fora disso o Esc
    // pertence a quem esta por baixo (o painel de dados do contato).
    localShow: {
      handler(isOpen) {
        if (isOpen) {
          this.bindEscapeKey();
        } else {
          this.unbindEscapeKey();
        }
      },
      immediate: true,
    },
  },

  beforeUnmount() {
    this.zipCodeLookupSequence += 1;
    this.unbindEscapeKey();
  },
  methods: {
    maskBrazilianPhone(value) {
      let digits = `${value || ''}`.replace(/\D/g, '');
      if (digits.startsWith('55') && [12, 13].includes(digits.length)) {
        digits = digits.slice(2);
      }
      digits = digits.slice(0, 11);
      if (digits.length > 10) {
        return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
      }
      if (digits.length > 6) {
        return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
      }
      if (digits.length > 2) {
        return digits.replace(/(\d{2})(\d{0,5})/, '($1) $2');
      }
      return digits;
    },
    maskDocument(value, type = this.documentType) {
      let digits = `${value || ''}`.replace(/\D/g, '');
      if (type === 'cpf') {
        digits = digits.slice(0, 11);
        return digits
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1-$2');
      }
      if (type === 'cnpj') {
        digits = digits.slice(0, 14);
        return digits
          .replace(/^(\d{2})(\d)/, '$1.$2')
          .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1/$2')
          .replace(/(\d{4})(\d)/, '$1-$2');
      }
      return digits.slice(0, 14);
    },
    maskZipCode(value) {
      const digits = `${value || ''}`.replace(/\D/g, '').slice(0, 8);
      return digits.length > 5
        ? digits.replace(/(\d{5})(\d{0,3})/, '$1-$2')
        : digits;
    },
    formatBirthDateDisplay(value) {
      const isoDate = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

      const digits = `${value || ''}`.replace(/\D/g, '').slice(0, 8);
      if (digits.length > 4) {
        return digits.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
      }
      if (digits.length > 2) {
        return digits.replace(/(\d{2})(\d{0,2})/, '$1/$2');
      }
      return digits;
    },
    isValidCpf(value) {
      const digits = `${value || ''}`.replace(/\D/g, '');
      if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
      const calculateDigit = base => {
        let sum = 0;
        let weight = base.length + 1;
        base.split('').forEach(digit => {
          sum += Number(digit) * weight;
          weight -= 1;
        });
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
      };
      return (
        calculateDigit(digits.slice(0, 9)) === Number(digits[9]) &&
        calculateDigit(digits.slice(0, 10)) === Number(digits[10])
      );
    },
    isValidCnpj(value) {
      const digits = `${value || ''}`.replace(/\D/g, '');
      if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;
      const calculateDigit = base => {
        const weights =
          base.length === 12
            ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
            : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const sum = weights.reduce(
          (total, weight, index) => total + Number(base[index]) * weight,
          0
        );
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
      };
      return (
        calculateDigit(digits.slice(0, 12)) === Number(digits[12]) &&
        calculateDigit(digits.slice(0, 13)) === Number(digits[13])
      );
    },
    onBasePhoneInput(event) {
      this.basePhone =
        this.dialCode === BRAZIL_DIAL_CODE
          ? this.maskBrazilianPhone(event.target.value)
          : `${event.target.value || ''}`.replace(/\D/g, '');
      event.target.value = this.basePhone;
    },
    resetClientPhoto() {
      this.photoUrl = '';
      this.photoLoadFailed = false;
    },
    onClientPhotoLoadError() {
      this.photoLoadFailed = true;
    },
    onDocumentInput(event) {
      this.document = this.maskDocument(event.target.value);
      event.target.value = this.document;
    },
    onDocumentTypeChange() {
      this.document = this.maskDocument(this.document);
    },
    onZipCodeInput(event) {
      this.zipCode = this.maskZipCode(event.target.value);
      event.target.value = this.zipCode;

      const digits = this.zipCode.replace(/\D/g, '');
      if (digits.length !== 8) {
        this.zipCodeLookupSequence += 1;
        return;
      }
      this.lookupAddressByZipCode(digits);
    },
    async lookupAddressByZipCode(digits) {
      this.zipCodeLookupSequence += 1;
      const requestSequence = this.zipCodeLookupSequence;
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${digits}/json/`
        );
        if (!response.ok) return;
        const addressData = await response.json();
        if (
          requestSequence !== this.zipCodeLookupSequence ||
          addressData.erro ||
          this.zipCode.replace(/\D/g, '') !== digits
        ) {
          return;
        }
        if (!this.address && addressData.logradouro) {
          this.address = addressData.logradouro;
        }
        if (!this.neighborhood && addressData.bairro) {
          this.neighborhood = addressData.bairro;
        }
        if (!this.city && addressData.localidade) {
          this.city = addressData.localidade;
        }
        if (addressData.uf) this.state = addressData.uf;
      } catch (error) {
        // A busca de endereço pelo CEP é uma conveniência; o formulário segue
        // funcionando normalmente se o serviço estiver indisponível.
      }
    },
    onBirthDateInput(event) {
      this.birthDateDisplay = this.formatBirthDateDisplay(event.target.value);
      event.target.value = this.birthDateDisplay;
      if (this.birthDateError) {
        this.birthDate = '';
        return;
      }
      const [day, month, year] = this.birthDateDisplay.split('/');
      this.birthDate = this.birthDateDisplay ? `${year}-${month}-${day}` : '';
    },
    onNativeBirthDateChange(event) {
      this.birthDate = event.target.value;
      this.birthDateDisplay = this.formatBirthDateDisplay(this.birthDate);
    },
    openBirthDatePicker() {
      const picker = this.$refs.birthDatePicker;
      try {
        if (typeof picker?.showPicker === 'function') {
          picker.showPicker();
        } else {
          picker?.click();
        }
      } catch (error) {
        picker?.click();
      }
    },
    setContactObject() {
      const parsedPhone = parsePhoneNumber(this.contact.phone_number || '');

      this.aceleresClient = null;
      this.resetClientPhoto();
      this.name = this.contact.name || '';
      this.responsibleId = '';
      this.sellers = [];
      this.canChangeResponsible = false;
      this.categories = [];

      if (parsedPhone?.countryCallingCode) {
        this.dialCode = `+${parsedPhone.countryCallingCode}`;
      } else {
        this.dialCode = BRAZIL_DIAL_CODE;
      }
      const fields = this.contact.custom_attributes || {};
      this.photoUrl =
        fields.photo_url ||
        this.contact.thumbnail ||
        this.contact.avatar_url ||
        '';
      this.nomeZoraxy = fields.nome_zoraxy || this.name;
      this.email = this.contact.email || fields.email || '';
      this.basePhone =
        this.dialCode === BRAZIL_DIAL_CODE
          ? this.maskBrazilianPhone(fields.phone || '')
          : fields.phone || '';
      this.documentType = fields.document_type || '';
      this.document = this.maskDocument(fields.document || '');
      const whatsappValue = fields.whatsapp || this.contact.phone_number || '';
      this.whatsapp =
        this.dialCode === BRAZIL_DIAL_CODE && !this.isGroupContact
          ? this.maskBrazilianPhone(whatsappValue)
          : whatsappValue;
      this.instagram = fields.instagram || '';
      this.tiktok = fields.tiktok || '';
      this.facebook = fields.facebook || '';
      this.telegram = fields.telegram || '';
      this.twitter = fields.twitter || '';
      this.linkedin = fields.linkedin || '';
      this.birthDate = fields.birth_date || '';
      this.birthDateDisplay = this.formatBirthDateDisplay(this.birthDate);
      this.address = fields.address || '';
      this.addressNumber = fields.address_number || '';
      this.addressComplement = fields.address_complement || '';
      this.neighborhood = fields.neighborhood || '';
      this.city = fields.city || '';
      this.state = fields.state || '';
      this.zipCode = this.maskZipCode(fields.zip_code || '');
      this.notes = fields.notes || '';
      this.categoriaAbc = fields.categoria_abc || '';
      this.categoria = fields.categoria || '';
      this.status = normalizeClientStatus(fields.status || '');
      this.responsible = '';
    },
    async loadAceleresClient() {
      if (!this.contact.id) return;

      this.aceleresClientRequest += 1;
      const requestId = this.aceleresClientRequest;
      try {
        const response = await ContactAPI.getAceleresClient(this.contact.id);
        if (requestId !== this.aceleresClientRequest) return;

        const client = response.data?.data || null;
        this.aceleresClient = client;
        const fields = client?.fields || {};
        this.name = fields.name ?? this.name;
        this.photoUrl =
          fields.photo_url ||
          this.contact.thumbnail ||
          this.contact.avatar_url ||
          '';
        this.photoLoadFailed = false;
        this.nomeZoraxy = fields.nome_zoraxy || this.name;
        this.basePhone =
          this.dialCode === BRAZIL_DIAL_CODE
            ? this.maskBrazilianPhone(fields.phone ?? this.basePhone)
            : fields.phone ?? this.basePhone;
        this.responsibleId = fields.user_id ? String(fields.user_id) : '';
        this.sellers = client?.sellers || [];
        this.canChangeResponsible = !!client?.can_change_responsible;
        this.categories = client?.categories || [];
        this.email = fields.email ?? this.email;
        this.documentType = fields.document_type ?? this.documentType;
        this.whatsapp = this.maskBrazilianPhone(
          fields.whatsapp ?? this.whatsapp
        );
        this.document = this.maskDocument(fields.document ?? this.document);
        this.instagram = fields.instagram ?? this.instagram;
        this.tiktok = fields.tiktok ?? this.tiktok;
        this.facebook = fields.facebook ?? this.facebook;
        this.telegram = fields.telegram ?? this.telegram;
        this.twitter = fields.twitter ?? this.twitter;
        this.linkedin = fields.linkedin ?? this.linkedin;
        this.birthDate = fields.birth_date ?? this.birthDate;
        this.birthDateDisplay = this.formatBirthDateDisplay(this.birthDate);
        this.address = fields.address ?? this.address;
        this.addressNumber = fields.address_number ?? this.addressNumber;
        this.addressComplement =
          fields.address_complement ?? this.addressComplement;
        this.neighborhood = fields.neighborhood ?? this.neighborhood;
        this.city = fields.city ?? this.city;
        this.state = fields.state ?? this.state;
        this.zipCode = this.maskZipCode(fields.zip_code ?? this.zipCode);
        this.categoriaAbc = fields.categoria_abc ?? this.categoriaAbc;
        this.categoria = fields.categoria ?? this.categoria;
        this.status = normalizeClientStatus(
          fields.status || this.status || 'cliente'
        );
        this.notes = fields.notes ?? this.notes;
        this.responsible = client?.seller || client?.responsible || '';
      } catch (error) {
        if (requestId === this.aceleresClientRequest) {
          this.aceleresClient = null;
        }
      }
    },
    // Aceleres 2026-08-17: o Esc fecha a edicao do contato. Antes o drawer nao
    // ouvia a tecla e ainda tinha `@click.stop`, entao nao havia clique fora
    // que o fechasse — so o botao de voltar. E, por ser um `role="dialog"`
    // visivel, ele tambem suprimia o Esc do painel de dados do contato
    // (ConversationSidebar), de modo que a tecla nao fechava nada.
    //
    // O listener e de captura e consome o evento: assim a mesma tecla nao
    // fecha o drawer e o painel de baixo de uma vez.
    onEscapeKey(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!this.localShow) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.onCancel();
    },
    bindEscapeKey() {
      if (this.escapeKeyHandler) return;
      this.escapeKeyHandler = event => this.onEscapeKey(event);
      document.addEventListener('keydown', this.escapeKeyHandler, true);
    },
    unbindEscapeKey() {
      if (!this.escapeKeyHandler) return;
      document.removeEventListener('keydown', this.escapeKeyHandler, true);
      this.escapeKeyHandler = null;
    },
    onCancel() {
      this.$emit('cancel');
    },
    onBack() {
      // Fecha somente o drawer de edição; o painel de dados do contato fica aberto.
      this.$emit('cancel');
    },
    onDelete() {
      this.$emit('delete');
    },
    async onSubmit() {
      if (!this.canSubmit) return;

      const name = this.name.trim();
      try {
        if (this.aceleresClient?.id) {
          const clientPayload = {
            client_id: this.aceleresClient.id,
            name,
            nome_zoraxy: this.nomeZoraxy || null,
            email: this.email || null,
            phone: this.basePhone || null,
            whatsapp: this.whatsapp || null,
            document: this.document || null,
            document_type: this.documentType || null,
            instagram: this.instagram || null,
            tiktok: this.tiktok || null,
            facebook: this.facebook || null,
            telegram: this.telegram || null,
            twitter: this.twitter || null,
            linkedin: this.linkedin || null,
            birth_date: this.birthDate || null,
            address: this.address || null,
            address_number: this.addressNumber || null,
            address_complement: this.addressComplement || null,
            neighborhood: this.neighborhood || null,
            city: this.city || null,
            state: this.state || null,
            zip_code: this.zipCode || null,
            categoria_abc: this.categoriaAbc || null,
            categoria: this.categoria || null,
            status: this.status || null,
            notes: this.notes || null,
            ...(this.canChangeResponsible
              ? { user_id: this.responsibleId || null }
              : {}),
          };

          await ContactAPI.updateAceleresClient(this.contact.id, clientPayload);
        } else {
          const customAttributes = {
            ...(this.contact.custom_attributes || {}),
            nome_zoraxy: this.nomeZoraxy || null,
            document: this.document || null,
            document_type: this.documentType || null,
            phone: this.basePhone || null,
            whatsapp: this.whatsapp || null,
            instagram: this.instagram || null,
            tiktok: this.tiktok || null,
            facebook: this.facebook || null,
            telegram: this.telegram || null,
            twitter: this.twitter || null,
            linkedin: this.linkedin || null,
            birth_date: this.birthDate || null,
            address: this.address || null,
            address_number: this.addressNumber || null,
            address_complement: this.addressComplement || null,
            neighborhood: this.neighborhood || null,
            city: this.city || null,
            state: this.state || null,
            zip_code: this.zipCode || null,
            notes: this.notes || null,
            categoria_abc: this.categoriaAbc || null,
            categoria: this.categoria || null,
            status: this.status || null,
          };

          await this.$store.dispatch('contacts/update', {
            id: this.contact.id,
            name,
            email: this.email || null,
            custom_attributes: customAttributes,
            additional_attributes: {
              ...this.contact.additional_attributes,
              country_code:
                this.countries.find(item => item.dial_code === this.dialCode)
                  ?.id || '',
            },
          });
        }

        await this.$store.dispatch('contacts/show', { id: this.contact.id });
        await this.$store.dispatch(
          'contacts/fetchContactableInbox',
          this.contact.id
        );
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        this.onCancel();
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const invalidAttrs = Array.isArray(error.data) ? error.data : [];
          if (invalidAttrs.includes('phone_number')) {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          } else {
            useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <Teleport :to="teleportTarget || 'body'">
    <Transition name="contact-edit-drawer">
      <aside
        v-if="localShow"
        class="contact-edit-drawer"
        :class="{ 'contact-edit-drawer--detached': !teleportTarget }"
        role="dialog"
        aria-modal="true"
        :aria-label="editContactTitle"
        @click.stop
      >
        <header class="contact-edit-drawer__header">
          <ChatwootButton
            v-tooltip.top="backLabel"
            type="button"
            variant="ghost"
            color="slate"
            size="lg"
            :aria-label="backLabel"
            @click.stop="onBack"
            icon="i-lucide-arrow-left size-6"
          />
          <h2 class="contact-edit-drawer__title">{{ editContactTitle }}</h2>
          <span class="contact-edit-drawer__header-spacer" />
        </header>

        <form class="contact-edit-drawer__form" @submit.prevent="onSubmit">
          <div class="contact-edit-drawer__content">
            <section
              class="contact-edit-drawer__group"
              aria-labelledby="contact-edit-contact-heading"
            >
              <h3
                id="contact-edit-contact-heading"
                class="contact-edit-drawer__section-title"
              >
                Contato
              </h3>
              <div class="contact-edit-drawer__fields-grid">
                <div
                  class="contact-edit-drawer__photo-field contact-edit-drawer__field--full"
                >
                  <div class="contact-edit-drawer__photo-avatar">
                    <img
                      v-if="clientPhotoSource"
                      :src="clientPhotoSource"
                      alt=""
                      @error="onClientPhotoLoadError"
                    />
                    <span v-else>{{ clientPhotoInitials }}</span>
                  </div>
                </div>
                <ChatwootInput
                  v-model="nomeZoraxy"
                  label="Nome Zoraxy"
                  type="text"
                  autocomplete="name"
                  placeholder="Nome exibido no Zoraxy"
                  maxlength="100"
                  size="sm"
                  class="w-full contact-edit-drawer__field--full"
                />
                <ChatwootInput
                  v-model="name"
                  label="Nome Acelerity"
                  type="text"
                  autocomplete="name"
                  placeholder="Nome completo"
                  maxlength="100"
                  size="sm"
                  class="w-full contact-edit-drawer__field--full"
                />
                <ChatwootInput
                  v-model="basePhone"
                  label="Telefone"
                  type="tel"
                  autocomplete="tel"
                  placeholder="(00) 0000-0000"
                  inputmode="tel"
                  maxlength="20"
                  size="sm"
                  class="w-full"
                  @input="onBasePhoneInput"
                />
                <ChatwootInput
                  v-model="email"
                  label="E-mail"
                  type="email"
                  autocomplete="email"
                  placeholder="exemplo@email.com"
                  maxlength="100"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="document"
                  label="Documento"
                  type="text"
                  :placeholder="
                    documentType === 'cnpj'
                      ? '00.000.000/0000-00'
                      : '000.000.000-00'
                  "
                  inputmode="numeric"
                  autocomplete="off"
                  size="sm"
                  class="w-full"
                  :message="documentError"
                  :message-type="documentError ? 'error' : 'info'"
                  @input="onDocumentInput"
                />
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label">
                    Tipo de documento
                  </span>
                  <DropdownSelect
                    v-model="documentType"
                    :options="documentTypeOptions"
                    placeholder="Selecione"
                    @update:model-value="onDocumentTypeChange"
                  />
                </label>
                <div class="contact-edit-drawer__date-field">
                  <ChatwootInput
                    v-model="birthDateDisplay"
                    label="Data de nascimento"
                    type="text"
                    placeholder="DD/MM/AAAA"
                    inputmode="numeric"
                    maxlength="10"
                    autocomplete="off"
                    size="sm"
                    class="w-full"
                    :message="birthDateError"
                    :message-type="birthDateError ? 'error' : 'info'"
                    @input="onBirthDateInput"
                  />
                  <button
                    type="button"
                    class="contact-edit-drawer__date-trigger"
                    aria-label="Abrir calendário de data de nascimento"
                    @click="openBirthDatePicker"
                  >
                    <span class="i-lucide-calendar-days" aria-hidden="true" />
                  </button>
                  <input
                    ref="birthDatePicker"
                    class="contact-edit-drawer__native-date-picker"
                    type="date"
                    :value="birthDate"
                    min="1900-01-01"
                    max="2100-12-31"
                    tabindex="-1"
                    aria-hidden="true"
                    @change="onNativeBirthDateChange"
                  />
                </div>
              </div>
            </section>

            <section
              class="contact-edit-drawer__group"
              aria-labelledby="contact-edit-address-heading"
            >
              <h3
                id="contact-edit-address-heading"
                class="contact-edit-drawer__section-title"
              >
                Endereço
              </h3>
              <div class="contact-edit-drawer__fields-grid">
                <ChatwootInput
                  v-model="zipCode"
                  label="CEP"
                  placeholder="00000-000"
                  inputmode="numeric"
                  size="sm"
                  class="w-full"
                  :message="zipCodeError"
                  :message-type="zipCodeError ? 'error' : 'info'"
                  @input="onZipCodeInput"
                />
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label">Estado</span>
                  <DropdownSelect
                    v-model="state"
                    :options="stateOptions"
                    placeholder="Selecione..."
                  />
                </label>
                <ChatwootInput
                  v-model="city"
                  label="Cidade"
                  placeholder="Digite a cidade"
                  maxlength="100"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="neighborhood"
                  label="Bairro"
                  placeholder="Digite o bairro"
                  maxlength="100"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="address"
                  label="Endereço"
                  placeholder="Digite o endereço"
                  maxlength="100"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="addressNumber"
                  label="Número"
                  placeholder="Nº (ex.: 123, 123A ou S/N)"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="addressComplement"
                  label="Complemento"
                  placeholder="Apto, bloco, etc."
                  maxlength="100"
                  size="sm"
                  class="w-full contact-edit-drawer__field--full"
                />
              </div>
            </section>

            <section
              class="contact-edit-drawer__group"
              aria-labelledby="contact-edit-social-heading"
            >
              <h3
                id="contact-edit-social-heading"
                class="contact-edit-drawer__section-title"
              >
                Redes sociais
              </h3>
              <div class="contact-edit-drawer__fields-grid">
                <ChatwootInput
                  v-model="instagram"
                  label="Instagram"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="tiktok"
                  label="TikTok"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="facebook"
                  label="Facebook"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="telegram"
                  label="Telegram"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="twitter"
                  label="X/Twitter"
                  size="sm"
                  class="w-full"
                />
                <ChatwootInput
                  v-model="linkedin"
                  label="LinkedIn"
                  size="sm"
                  class="w-full"
                />
              </div>
            </section>

            <section
              class="contact-edit-drawer__group"
              aria-labelledby="contact-edit-account-heading"
            >
              <h3
                id="contact-edit-account-heading"
                class="contact-edit-drawer__section-title"
              >
                Classificação e atendimento
              </h3>
              <div class="contact-edit-drawer__fields-grid">
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label"
                    >Categoria</span
                  >
                  <DropdownSelect
                    v-model="categoria"
                    :options="categoryOptions"
                    placeholder="Selecione"
                  />
                </label>
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label"
                    >Curva ABC</span
                  >
                  <DropdownSelect
                    v-model="categoriaAbc"
                    :options="abcOptions"
                    placeholder="Selecione"
                  />
                </label>
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label">Status</span>
                  <DropdownSelect
                    v-model="status"
                    :options="statusOptions"
                    placeholder="Selecione"
                  />
                </label>
                <label class="contact-edit-drawer__select-field">
                  <span class="contact-edit-drawer__select-label"
                    >Vendedor responsável</span
                  >
                  <DropdownSelect
                    :model-value="responsibleDropdownValue"
                    :options="responsibleOptions"
                    placeholder="Selecione"
                    :disabled="!canChangeResponsible"
                    @update:model-value="responsibleId = $event"
                  />
                </label>
                <label
                  class="contact-edit-drawer__textarea-field contact-edit-drawer__field--full"
                >
                  <span class="contact-edit-drawer__select-label"
                    >Observações</span
                  >
                  <textarea
                    v-model="notes"
                    class="contact-edit-drawer__control contact-edit-drawer__textarea"
                    rows="3"
                    maxlength="200"
                    placeholder="Observações sobre o cliente..."
                  />
                </label>
              </div>
            </section>
          </div>

          <button
            v-tooltip="$t('CONTACT_FORM.FORM.SUBMIT')"
            type="submit"
            class="contact-edit-drawer__submit"
            :disabled="uiFlags.isUpdating || !canSubmit"
            :aria-label="$t('CONTACT_FORM.FORM.SUBMIT')"
          >
            <span
              v-if="uiFlags.isUpdating"
              class="i-lucide-loader-circle contact-edit-drawer__submit-loader"
            />
            <span v-else class="i-lucide-check" />
          </button>
        </form>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Aceleres 2026-08-17: mesma caixa da lista de mídias (ver
   `.contact-details-media-modal` em ContactInfo.vue) — cobre o painel de
   contato inteiro, em vez de ser uma gaveta presa à janela. As duas telas
   abrem do mesmo sítio e agora têm a mesma medida. */
.contact-edit-drawer {
  position: absolute;
  inset: 0;
  /* Aceleres 2026-08-17: as medidas do formulário passam a olhar a LARGURA
     DESTA CAIXA, não a da janela — ver os `@container` no fim do ficheiro. */
  container-type: inline-size;
  z-index: 2147483647;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid rgb(var(--border-weak));
  background: #ffffff;
  box-shadow: none;
}

/* Reserva: sem o container do painel, mantém o comportamento antigo — a mesma
   reserva que `.contact-details-media-modal--detached` tem. */
.contact-edit-drawer--detached {
  position: fixed;
  inset: 0 0 auto auto;
  width: min(100vw, 987px);
  height: 100vh;
}

.contact-edit-drawer__header {
  display: flex;
  height: 64px;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  background: #ffffff;
}

.contact-edit-drawer__title {
  min-width: 0;
  flex: 1 1 auto;
  margin: 0;
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-edit-drawer__header-spacer {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: rgb(var(--slate-12));
}

.contact-edit-drawer__form {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
}

.contact-edit-drawer__content {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px 24px 112px;
}

.contact-edit-drawer__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 10px;
  background: rgb(var(--slate-2));
}

.contact-edit-drawer__group + .contact-edit-drawer__group {
  margin-top: 12px;
}

.contact-edit-drawer__section-title {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.contact-edit-drawer__fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.contact-edit-drawer__fields-grid > .contact-edit-drawer__field--full {
  grid-column: 1 / -1;
}

.contact-edit-drawer__photo-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.contact-edit-drawer__photo-avatar {
  display: flex;
  width: 88px;
  height: 88px;
  flex: 0 0 88px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  border: 1px solid rgb(var(--border-weak));
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  font-size: 1.5rem;
  font-weight: 600;
}

.contact-edit-drawer__photo-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-edit-drawer__select-field,
.contact-edit-drawer__textarea-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.contact-edit-drawer__select-field {
  margin: 0;
}

.contact-edit-drawer__content :deep(.relative > label),
.contact-edit-drawer__select-label {
  margin: 0 0 0.3rem;
  color: rgb(var(--slate-11));
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.contact-edit-drawer__content
  :deep(input:not(.contact-edit-drawer__native-date-picker)),
.contact-edit-drawer__control {
  box-sizing: border-box;
  width: 100%;
  height: 40px !important;
  min-height: 40px;
  padding: 0.625rem 0.75rem !important;
  border: 1px solid var(--border-color, rgb(var(--border-weak))) !important;
  border-radius: 10px !important;
  outline: none !important;
  background: var(--bg-tertiary, #ffffff) !important;
  color: var(--text-primary, rgb(var(--slate-12))) !important;
  box-shadow: none;
  font-family: inherit;
  font-size: 0.9rem !important;
  font-weight: 400;
  line-height: 1.2;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.contact-edit-drawer__content
  :deep(input:not(.contact-edit-drawer__native-date-picker)):focus,
.contact-edit-drawer__control:focus {
  border-color: #f87171 !important;
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15) !important;
}

.contact-edit-drawer__date-field {
  position: relative;
  min-width: 0;
}

.contact-edit-drawer__date-field
  :deep(input:not(.contact-edit-drawer__native-date-picker)) {
  padding-right: 2.75rem !important;
}

.contact-edit-drawer__date-trigger {
  position: absolute;
  right: 7px;
  top: 26px;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: pointer;
}

.contact-edit-drawer__date-trigger:hover,
.contact-edit-drawer__date-trigger:focus-visible {
  background: rgb(var(--slate-2));
  outline: none;
}

.contact-edit-drawer__native-date-picker {
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 1px;
  height: 1px;
  padding: 0;
  opacity: 0;
  pointer-events: none;
}

.contact-edit-drawer__textarea {
  height: auto !important;
  min-height: 88px;
  padding-top: 0.625rem !important;
  line-height: 1.4;
  resize: none;
}

.contact-edit-drawer__submit {
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 24px;
  display: inline-flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  transform: translateX(-50%);
  border: 0;
  border-radius: 9999px;
  background: #1d1d1d;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  transition:
    background 160ms ease,
    transform 160ms ease;
}

.contact-edit-drawer__submit:hover,
.contact-edit-drawer__submit:focus-visible {
  background: #000000;
  outline: none;
}

.contact-edit-drawer__submit:active {
  transform: translateX(-50%) scale(0.96);
}

.contact-edit-drawer__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.contact-edit-drawer__submit span {
  width: 28px;
  height: 28px;
  font-size: 28px;
}

.contact-edit-drawer__submit-loader {
  animation: contact-edit-spin 900ms linear infinite;
}

@keyframes contact-edit-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Match the one-column form layout used by Chatwoot dialogs at narrow widths. */
@container (max-width: 460px) {
  .contact-edit-drawer__header {
    height: 56px;
    padding: 0 12px;
  }

  .contact-edit-drawer__title {
    font-size: 1rem;
    font-weight: 600;
  }

  .contact-edit-drawer__content {
    padding: 16px 16px 88px;
  }

  .contact-edit-drawer__group {
    padding: 12px;
  }

  .contact-edit-drawer__group + .contact-edit-drawer__group {
    margin-top: 12px;
  }

  .contact-edit-drawer__title {
    font-size: 1rem;
  }

  .contact-edit-drawer__fields-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .contact-edit-drawer__submit {
    bottom: 16px;
    width: 56px;
    height: 56px;
  }

  .contact-edit-drawer__submit span {
    width: 22px;
    height: 22px;
    font-size: 22px;
  }
}

.contact-edit-drawer-enter-active,
.contact-edit-drawer-leave-active {
  transition: transform 220ms ease-in-out;
}

.contact-edit-drawer-enter-from,
.contact-edit-drawer-leave-to {
  transform: translateX(100%);
}

@keyframes contact-edit-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
